import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import SparklesIcon from "lucide-react/dist/esm/icons/sparkles";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";

export type SlashCommandItem = {
  id: string;
  label: string;
  description?: string;
  badge?: string;
};

export interface SlashCommandMenuRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

type SlashCommandMenuProps = SuggestionProps<SlashCommandItem> & {
  heading: string;
  emptyMessage: string;
  isLoading: boolean;
};

export const SlashCommandMenu = forwardRef<SlashCommandMenuRef, SlashCommandMenuProps>(function SlashCommandMenu(
  { items, command, heading, emptyMessage, isLoading, editor },
  ref,
) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listId = useId();
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.min(selectedIndex, Math.max(0, items.length - 1));

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);
  useEffect(() => {
    itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);
  useEffect(() => {
    const input = editor.view.dom;
    input.setAttribute("aria-controls", listId);
    input.setAttribute("aria-expanded", "true");
    input.setAttribute("aria-haspopup", "listbox");
    if (!isLoading && items.length > 0) input.setAttribute("aria-activedescendant", `${listId}-${activeIndex}`);
    else input.removeAttribute("aria-activedescendant");
    return () => {
      for (const name of ["aria-controls", "aria-expanded", "aria-haspopup", "aria-activedescendant"]) {
        input.removeAttribute(name);
      }
    };
  }, [editor, listId, activeIndex, items.length, isLoading]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.isComposing || event.shiftKey || !["ArrowUp", "ArrowDown", "Enter", "Tab"].includes(event.key)) {
        return false;
      }
      if (event.key === "Tab" && (isLoading || items.length === 0)) return false;
      event.preventDefault();
      if (!isLoading && items.length > 0) {
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          const direction = event.key === "ArrowUp" ? -1 : 1;
          setSelectedIndex((index) => (index + direction + items.length) % items.length);
        } else {
          command(items[activeIndex]!);
        }
      }
      return true;
    },
  }));

  return (
    <div
      data-slash-command-suggestion="true"
      id={listId}
      role="listbox"
      aria-label={heading}
      aria-busy={isLoading}
      className="w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-lg bg-popover text-popover-foreground"
    >
      <div className="flex items-center justify-between border-b px-3 py-2 text-xs text-muted-foreground">
        <span className="font-medium">{heading}</span>
        <span className="text-[10px]">↑↓ navigate · ↵ select · esc close</span>
      </div>
      {isLoading ? (
        <div role="status" className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
          <Loader2Icon className="size-3.5 animate-spin" aria-hidden /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div role="status" className="px-3 py-3 text-xs text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto p-1">
          {items.map((item, index) => (
            <button
              key={item.id}
              id={`${listId}-${index}`}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              type="button"
              tabIndex={-1}
              role="option"
              aria-selected={index === activeIndex}
              className={`flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-left text-xs ${
                index === activeIndex
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              }`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => command(item)}
            >
              <span className="flex size-7 shrink-0 items-center justify-center rounded-md border bg-muted/50">
                <SparklesIcon className="size-3.5 text-primary" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="truncate font-medium">{item.label}</span>
                  {item.badge && (
                    <span className="shrink-0 rounded-full border px-1.5 text-[10px] text-muted-foreground">
                      {item.badge}
                    </span>
                  )}
                </span>
                {item.description && (
                  <span className="mt-0.5 block truncate text-muted-foreground">{item.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
